import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MButton } from "@/components/ui/atoms/MButtons";

import { useLocation } from "react-router-dom";
import { IconCircleCheck } from "@tabler/icons-react";
import { useProfileUpdate, getMemberInfo } from "@/hooks/useMembers";

import { useManagedMembersContext } from "@/context/ManagedMembersContext";
import { clearStoragePreservingTours } from "@/utils/storage-utils";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

const DeleteConfirm: React.FC = () => {
  const navigate = useNavigate();
  const { updateMemberInLocalStorage } = useProfileUpdate();
  const memberInfo = getMemberInfo(true);

  const location = useLocation();
  // Try to get name, type, and context from location.state
  const name = location?.state?.name || "Member";
  const memberType = location?.state?.memberType; // 'fo' or 'fm'
  // Force 'self-exit' message if action is 'exit' in state (handle nested state)
  const action = location?.state?.action || location?.state?.state?.action;
  const deletedType =
    action === "exit"
      ? "self-exit"
      : action === "delete" && location?.state?.newOwnerId
      ? "self-delete"
      : location?.state?.deletedType || "default";

  const { fetchManagedMembers } = useManagedMembersContext();

  useEffect(() => {
    if (!memberInfo?.id) return;

    if (
      deletedType === "self-delete" ||
      deletedType === "fo-delete" ||
      deletedType === "default"
    ) {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
        screenName: ANALYTICS_SCREEN_NAMES.FAMILY_INFO_MEMBER_DELETE_COMPLETE,
      });
    }
  }, [memberInfo?.id, deletedType]);
  const handleDone = async () => {
    await fetchManagedMembers(true); // force refetch
    if (action === "exit" && location?.state?.memberId) {
      updateMemberInLocalStorage(location.state.memberId);
    }
    // Get selectedMember from localStorage
    let selectedMember = null;
    try {
      selectedMember = JSON.parse(
        localStorage.getItem("selectedMember") || "null"
      );
    } catch {
      /* noop -- selectedMember payload parse failure is non-fatal */
    }
    const targetMemberId = location?.state?.memberId;
    if (selectedMember && selectedMember.id === targetMemberId) {
      localStorage.removeItem("selectedMember");
    }
    if (deletedType === "self-delete" || deletedType === "fo-delete") {
      clearStoragePreservingTours();
      window.location.href = "/signup";
    } else if (deletedType === "self-exit" && memberType === "fm") {
      // Only FM goes to new-subscription
      navigate("/new-subscription");
    } else {
      // Full reload (not navigate) so the deleted member is flushed from every
      // cache and context. Lands on the dashboard rather than the family list —
      // the member just removed is no longer there to return to.
      window.location.href = "/dashboard";
    }
  };

  // Mapping for all deletedType scenarios
  const deleteMessages: Record<
    string,
    {
      title: (name: string, familyName?: string) => string;
      sub1: (name: string, familyName?: string) => string;
    }
  > = {
    "registered-remove": {
      title: (name) => `${name} has been removed from your family`,
      sub1: () => `We’ve sent them a notification about this change.`,
    },
    "self-delete": {
      title: () => `Your account has been deleted`,
      sub1: () =>
        `If you choose to return, you'll need to create a new Maatra account to get started.`,
    },
    "self-exit": {
      title: (_name, familyName) =>
        `You’ve left ${familyName || "your family"}`,
      sub1: () =>
        `You’re now the Family Owner of your own family. You can start adding family members`,
    },
    "fo-exit": {
      title: (_name) => `You’ve left 'your family'`,
      sub1: () =>
        `You’re now the Family Owner of your own private space. Start a new family group anytime by adding members.`,
    },
    "fo-delete": {
      title: (_name) => `Your account has been deleted`,
      sub1: () =>
        `If you choose to return, you’ll need to create a new Maatra account to get started.`,
    },
    default: {
      title: (name) => `${name} account has been deleted from Maatra`,
      sub1: () =>
        `Their profile and health records have been permanently removed.`,
    },
  };

  // Always use self-exit message if action is exit
  const familyName = location?.state?.familyName;
  const { title, sub1 } =
    action === "exit"
      ? {
          title: (n: string) =>
            deleteMessages["self-exit"].title(n, familyName),
          sub1: (n: string) => deleteMessages["self-exit"].sub1(n, familyName),
        }
      : deleteMessages[deletedType] || deleteMessages["default"];

  return (
    <div className="flex-col items-center justify-center min-h-screen bg-white px-4 curvy-gradient-profile-success">
      <div className="profile-delete-row" />
      <div className="profile-delete-col">
        <div>
          <IconCircleCheck size={72} stroke={2.2} color="#C8B7E0" />
        </div>
        <div style={{ textAlign: "center" }} className="body-xl-bold">
          {title(name)}
        </div>
        <div
          style={{ textAlign: "center", lineHeight: 1.5 }}
          className="manageAccessEmptyText"
        >
          {sub1(name)}
        </div>
      </div>
      <div className="confirm-footer">
        <MButton
          variant="filled"
          size="lg"
          className="w-full"
          onClick={handleDone}
        >
          {deletedType === "self-exit" && memberType === "fm"
            ? "Manage subscription"
            : "Done"}
        </MButton>
      </div>
    </div>
  );
};

export default DeleteConfirm;
